// ANGULAR CORE

import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { providePrimeNG } from "primeng/config";
import { PlatformInterceptor } from "./_interceptors/platform.interceptor";

import { NgModule } from "@angular/core";
import { FormsModule } from "@angular/forms";

import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  HttpClientModule,
} from "@angular/common/http";

import { ButtonModule } from "primeng/button";
import { MenubarModule } from "primeng/menubar";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { BlockUIModule } from "primeng/blockui";
import { SelectModule } from "primeng/select";

// ENVIRONMENT

import { environment } from "../environments/environment";
import { instanceType } from "./utils";

// CACHE BUSTING

import cacheBusting from "../../assets-cache-busting.json";

// TRANSLATION

import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { MultiTranslateHttpLoader } from "ngx-translate-multi-http-loader";
//Global Loader
import { GlobalLoaderComponent } from "./components/global-loader/global-loader.component";

// https://github.com/ngx-translate/core
// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpBackend) {
  return new MultiTranslateHttpLoader(http, [
    {
      prefix: "./assets" + "/i18n/" + instanceType() + "/",
      suffix: ".json?_=" + cacheBusting["i18n"],
      optional: true,
    },
    {
      prefix: "./assets" + "/i18n/" + "default" + "/",
      suffix: ".json?_=" + cacheBusting["i18n"],
    },
  ]);
}

import { TableModule } from "primeng/table";
import { CompMenuBar } from "./components/comp-nav-bar/comp-nav-bar.component";

import { NbFormatter } from "./_pipes/nbformater.pipe";

// PAGES COMPONENTS

import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";

import { PageNotfoundComponent } from "./pages/page-notfound/page-notfound.component";
import { DataViewModule } from "primeng/dataview";
import { AddHeaderInterceptor } from "./_services/http-client";
import { definePreset } from "@primeng/themes";
import Aura from "@primeng/themes/aura";
import { AppSidebarComponent } from "./components/app-sidebar/app-sidebar.component";
import { AppNavbarComponent } from "./components/app-navbar/app-navbar.component";
import { AppLayoutComponent } from "./components/app-layout/app-layout.component";
import { TableComponent } from "./components/table/table.component";
import { BreadcrumbsComponent } from "./components/breadcrumbs/breadcrumbs.component";
import { LoadingInterceptor } from "./_interceptors/loading.interceptor";
import { MessageService } from "primeng/api";
import { DashboardComponent } from "./pages/dashboard/dashboard.component";
import { PrimeNGModule } from "./pages/shared/prime.module";
import { FilterChipsComponent } from "./components/filter-chips/filter-chips.component";
import { ConfigurableFilterBarComponent } from "./components/configurable-filter-bar/configurable-filter-bar.component";
import { ConfigurableFilterComponent } from "./components/configurable-filter/configurable-filter.component";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";
import { AuthNotificationsComponent } from "./pages/e-notification/auth-notification/auth-notifications.component";
import { PublicNotificationsComponent } from "./pages/e-notification/public-notifications/public-notifications.component";
import { PdfViewerComponent } from "./components/pdf-viewer/pdf-viewer.component";
import { PublicLayoutComponent } from "./pages/e-notification/layouts/public-layout/public-layout.component";
import { AuthLayoutComponent } from "./pages/e-notification/layouts/auth-layout/auth-layout.component";
import { AuthNotificationsListComponent } from "./pages/e-notification/auth-notification/auth-notifications-list/auth-notifications-list.component";
import { AuthInterceptor } from "./_interceptors/auth.interceptor";
import { CognitoSyncComponent } from "./pages/cognito-sync/cognito-sync.component";

const WipoThemePreset = definePreset(Aura, {
  primitive: {
    fontFamily: '"Noto Sans", "Segoe UI", Roboto, Arial, sans-serif',
    fontSize: "14px",
  },
  semantic: {
    primary: {
      50: "#e6f0f9",
      100: "#cce0f3",
      200: "#99c2e6",
      300: "#66a3da",
      400: "#3385cd",
      500: "#0067c0", // WIPO blue
      600: "#0052a3",
      700: "#003e87",
      800: "#00296a",
      900: "#00154e",
      950: "#000a32",
    },
    text: {
      fontWeight: "400",
      lineHeight: "1.5",
    },
    heading: {
      fontWeight: "600",
      lineHeight: "1.2",
      color: "#0067c0",
    },
  },
});
@NgModule({
  declarations: [
    CompMenuBar,
    AppComponent,
    PageNotfoundComponent,
    PdfViewerComponent,
    NbFormatter,
    DashboardComponent,
    CognitoSyncComponent,
    PublicLayoutComponent,
    AuthLayoutComponent,
    AuthNotificationsComponent,
    AuthNotificationsListComponent,
    PublicNotificationsComponent,
    FilterChipsComponent,
    ConfigurableFilterBarComponent,
    ConfigurableFilterComponent,
  ],
  imports: [
    AppLayoutComponent,
    TableComponent,
    AppNavbarComponent,
    AppSidebarComponent,
    GlobalLoaderComponent,
    BrowserModule,
    BrowserAnimationsModule,
    ButtonModule,
    BlockUIModule,
    ProgressSpinnerModule,
    MenubarModule,
    SelectModule,
    AppRoutingModule,
    FormsModule,
    DataViewModule,
    TableModule,
    BreadcrumbsComponent,
    NgxExtendedPdfViewerModule,
    HttpClientModule,
    // https://github.com/ngx-translate/core
    TranslateModule.forRoot({
      defaultLanguage: "en",
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend],
      },
    }),
    // CUSTOM_IMPORTS
    PrimeNGModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddHeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    MessageService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PlatformInterceptor,
      multi: true,
    },
    providePrimeNG({
      theme: {
        preset: WipoThemePreset,
        options: {
          prefix: "p",
          darkModeSelector: ".app-dark",
        },
      },
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
